import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';

import { IrpfInfoComponent } from '@app/ptrab/irpf/components/irpf-info/irpf-info/irpf-info.component';
import { IrsInfoComponent } from '@app/ptrab/irpf/components/irpf-info/irs-info/irs-info.component';
import { PTRAB_PERMISSIONS, PTRAB_SECTIONS_PERMISSIONS } from '@app/ptrab/ptrab-permissions.const';
import { PtrabGuardService } from '@app/ptrab/services/session/ptrab.guard.service';
import { PageExtras } from '@app/services/analytics/models/analytics-engine.interface';
import { PagesAnalytics } from '@app/services/analytics/models/analytics.enum';
import { AuthGuardPublicService } from '@app/services/guard/auth.guard.public.service';
import { ExworkersGuardService } from '@app/services/guard/exworkers.guard.service';
import { OnBoardingGuardService } from '@app/services/guard/on-boarding.guard.service';
import { PermissionsGuardService } from '@app/services/guard/permissions.guard.service';
import { PublicGuardService } from '@app/services/guard/public.guard.service';
import { WebViewGuardService } from '@app/services/guard/webview.guard.service';
import { getUrlFromMap } from '@app/shared/enums/pages/pages.urls';
import { Permissions } from '@app/shared/enums/permissions/permissions.enum';
import { AuthGuardService } from '@services/guard/auth.guard.service';
import { DeactivateGuardService } from '@services/guard/deactivate.guard.service';
import { PlatformGuardService } from '@services/guard/platform.guard.service';
import { PAGES } from '@shared/enums/pages/pages.enum';

import { MANAGE_VACATIONS_PERMISSIONS } from './manage-vacations/manage-vacations.constants';
import { VACATIONS_PERMISSIONS } from './vacations/vacations.const';

const viewerRoutes: Routes = [
  {
    path: 'p/:id',
    redirectTo: 'viewer/:id',
    pathMatch: 'full'
  },
  {
    path: 'e/:id',
    redirectTo: 'examples?item_id=:id',
    pathMatch: 'full'
  },
  {
    path: 'viewer/:id',
    loadChildren: () => import('./viewer/viewer.module').then((m) => m.ViewerPageModule),
    canActivate: [PublicGuardService],
    data: {
      pageAnalytics: PagesAnalytics.PUBLIC_CONTENT
    }
  }
];

const routes: Routes = [
  {
    path: 'home',
    loadChildren: () => import('./home/home.module').then((m) => m.HomePageModule),
    canActivate: [AuthGuardService, OnBoardingGuardService, ExworkersGuardService],
    canLoad: [WebViewGuardService],
    data: {
      pageAnalytics: PagesAnalytics.HOME,
      pageExtras: {
        pageType: 'home'
      } as PageExtras
    }
  },
  {
    path: 'auth',
    loadChildren: () => import('./auth/auth.module').then((m) => m.AuthPageModule)
  },
  {
    path: 'login',
    loadChildren: () => import('./login/login.module').then((m) => m.LoginPageModule)
  },
  {
    path: 'personal-data',
    loadChildren: () => import('./personal-data/personal-data.module').then((m) => m.PersonalDataPageModule),
    canLoad: [PtrabGuardService],
    canActivate: [PermissionsGuardService, PtrabGuardService, OnBoardingGuardService],
    data: {
      allowGoBack: true,
      pagePermissions: PTRAB_SECTIONS_PERMISSIONS.PERSONAL_DATA,
      pageToRedirectAfterLogin: getUrlFromMap(PAGES.PERSONAL_DATA),
      redirectTo: PAGES.HOME,
      pageAnalytics: PagesAnalytics.PERSONAL_DATA
    }
  },
  {
    path: 'model',
    loadChildren: () => import('./model/model.module').then((m) => m.ModelPageModule),
    canLoad: [AuthGuardService],
    canActivate: [OnBoardingGuardService, ExworkersGuardService],
    data: {
      pageAnalytics: PagesAnalytics.MODEL,
      pageExtras: {
        pageType: 'modelo'
      } as PageExtras
    }
  },
  {
    path: 'terms-and-conditions-inter',
    loadChildren: () =>
      import('./terms-and-conditions-inter/terms-and-conditions-inter.module').then(
        (m) => m.TermsAndConditionsInterPageModule
      ),
    data: {
      pageAnalytics: PagesAnalytics.TERMS_AND_CONDITIONS
    }
  },
  {
    path: 'terms-and-conditions',
    loadChildren: () =>
      import('./terms-and-conditions/terms-and-conditions.module').then((m) => m.TermsAndConditionsPageModule),
    canLoad: [AuthGuardService],
    data: {
      allowGoBack: false,
      pageAnalytics: PagesAnalytics.TERMS_AND_CONDITIONS
    }
  },
  {
    path: 'mobileweb-landing',
    loadChildren: () =>
      import('./mobileweb-landing/mobileweb-landing.module').then((m) => m.MobilewebLandingPageModule),
    data: {
      pageAnalytics: PagesAnalytics.MOBILE_LANDING
    }
  },
  {
    path: 'irpf-info',
    canActivate: [AuthGuardPublicService, OnBoardingGuardService, ExworkersGuardService],
    component: IrpfInfoComponent,
    data: {
      allowGoBack: true,
      pageAnalytics: PagesAnalytics.PIT_INFO
    }
  },
  {
    path: 'irs-info',
    canActivate: [AuthGuardPublicService, OnBoardingGuardService, ExworkersGuardService],
    component: IrsInfoComponent,
    data: {
      allowGoBack: true,
      pageAnalytics: PagesAnalytics.PIT_INFO_PT
    }
  },
  {
    path: 'logout',
    loadChildren: () => import('./logout/logout.module').then((m) => m.LogoutPageModule),
    data: {
      pageAnalytics: PagesAnalytics.LOGOUT
    }
  },
  {
    path: 'offline',
    loadChildren: () => import('./offline/offline.module').then((m) => m.OfflinePageModule),
    canDeactivate: [DeactivateGuardService],
    canActivate: [ExworkersGuardService],
    data: {
      pageAnalytics: PagesAnalytics.OFFLINE
    }
  },
  {
    path: 'banned',
    loadChildren: () => import('./banned/banned.module').then((m) => m.BannedPageModule),
    canDeactivate: [DeactivateGuardService],
    canActivate: [ExworkersGuardService],
    data: {
      pageAnalytics: PagesAnalytics.BANNED
    }
  },
  {
    path: 'maintenance',
    loadChildren: () => import('./maintenance/maintenance.module').then((m) => m.MaintenancePageModule),
    canDeactivate: [DeactivateGuardService],
    data: {
      pageAnalytics: PagesAnalytics.MAINTENANCE
    }
  },
  {
    path: 'error',
    loadChildren: () => import('./error/error.module').then((m) => m.ErrorPageModule),
    canDeactivate: [DeactivateGuardService],
    data: {
      pageAnalytics: PagesAnalytics.ERROR,
      pageExtras: {
        pageType: 'error'
      } as PageExtras
    }
  },
  {
    path: 'outdated-app-version',
    loadChildren: () =>
      import('./outdated-app-version/outdated-app-version.module').then((m) => m.OutdatedAppVersionPageModule),
    canActivate: [ExworkersGuardService],
    data: {
      pageAnalytics: PagesAnalytics.OUTDATED_VERSION
    }
  },
  {
    path: 'examples',
    loadChildren: () => import('./examples/examples.module').then((m) => m.ExamplesPageModule),
    canLoad: [AuthGuardService],
    canActivate: [OnBoardingGuardService, ExworkersGuardService],
    data: {
      pageAnalytics: PagesAnalytics.EXAMPLES,
      pageExtras: {
        pageType: 'ejemplos'
      } as PageExtras
    }
  },
  {
    path: 'publication/:id',
    loadChildren: () => import('./item-detail/item-detail.module').then((m) => m.ItemDetailPageModule),
    canLoad: [AuthGuardService],
    canActivate: [OnBoardingGuardService, ExworkersGuardService],
    data: {
      type: PAGES.ITEM,
      allowGoBack: true,
      pageAnalytics: PagesAnalytics.PUBLICATION,
      pageExtras: {
        pageType: 'publicaciones'
      } as PageExtras
    }
  },
  {
    path: 'publication/:id/:comment_id',
    loadChildren: () => import('./item-detail/item-detail.module').then((m) => m.ItemDetailPageModule),
    canLoad: [AuthGuardService],
    canActivate: [OnBoardingGuardService, ExworkersGuardService],
    data: {
      type: PAGES.ITEM,
      allowGoBack: true,
      pageAnalytics: PagesAnalytics.PUBLICATION,
      pageExtras: {
        pageType: 'publicaciones'
      } as PageExtras
    }
  },
  {
    path: 'example/:id',
    loadChildren: () => import('./item-detail/item-detail.module').then((m) => m.ItemDetailPageModule),
    canLoad: [AuthGuardService],
    canActivate: [OnBoardingGuardService, ExworkersGuardService],
    data: {
      type: PAGES.ITEM_EXAMPLE,
      allowGoBack: true,
      pageAnalytics: PagesAnalytics.EXAMPLE,
      pageExtras: {
        pageType: 'ejemplos'
      } as PageExtras
    }
  },
  {
    path: 'example/:id/:comment_id',
    loadChildren: () => import('./item-detail/item-detail.module').then((m) => m.ItemDetailPageModule),
    canLoad: [AuthGuardService],
    canActivate: [OnBoardingGuardService, ExworkersGuardService],
    data: {
      type: PAGES.ITEM_EXAMPLE,
      allowGoBack: true,
      pageAnalytics: PagesAnalytics.EXAMPLE,
      pageExtras: {
        pageType: 'ejemplos'
      } as PageExtras
    }
  },
  ...viewerRoutes,
  {
    path: 'search-results',
    loadChildren: () => import('./search-results/search-results.module').then((m) => m.SearchResultsPageModule),
    canLoad: [AuthGuardService],
    canActivate: [OnBoardingGuardService, ExworkersGuardService],
    data: {
      pageAnalytics: PagesAnalytics.SEARCH_RESULTS,
      pageExtras: {
        pageType: 'busqueda'
      } as PageExtras
    }
  },
  {
    path: 'examples/:type',
    loadChildren: () =>
      import('./examples-category-detail/examples-category-detail.page.module').then(
        (m) => m.ExamplesCategoryDetailPageModule
      ),
    canLoad: [AuthGuardService],
    canActivate: [OnBoardingGuardService, ExworkersGuardService],
    data: {
      allowGoBack: true,
      pageAnalytics: PagesAnalytics.EXAMPLES,
      pageExtras: {
        pageType: 'ejemplos'
      } as PageExtras
    }
  },
  {
    path: 'device-info',
    loadChildren: () => import('./device-info/device-info.module').then((m) => m.DeviceInfoPageModule),
    canLoad: [AuthGuardService],
    canActivate: [OnBoardingGuardService, ExworkersGuardService],
    data: {
      allowGoBack: false,
      pageAnalytics: PagesAnalytics.DEVICE_INFO
    }
  },
  {
    path: 'notifications',
    loadChildren: () => import('./notifications/notifications.module').then((m) => m.NotificationsPageModule),
    canLoad: [AuthGuardService],
    canActivate: [OnBoardingGuardService, ExworkersGuardService],
    data: {
      allowGoBack: true,
      pageAnalytics: PagesAnalytics.NOTIFICATIONS,
      pageExtras: {
        pageType: 'informaciondispositivo'
      } as PageExtras
    }
  },
  {
    path: 'cookies',
    loadChildren: () => import('./cookies/cookies.module').then((m) => m.CookiesPageModule),
    canLoad: [AuthGuardService],
    data: {
      allowGoBack: false,
      pageAnalytics: PagesAnalytics.COOKIES
    }
  },
  {
    path: 'not-found',
    loadChildren: () => import('./not-found/not-found.module').then((m) => m.NotFoundPageModule),
    data: {
      pageAnalytics: PagesAnalytics.NOT_FOUND
    }
  },
  {
    path: 'privacy',
    loadChildren: () => import('./privacy/privacy.module').then((m) => m.PrivacyPageModule),
    data: {
      pageAnalytics: PagesAnalytics.PRIVACY
    }
  },
  {
    path: 'configuration',
    loadChildren: () => import('./configuration/configuration.module').then((m) => m.ConfigurationPageModule),
    canActivate: [OnBoardingGuardService, ExworkersGuardService],
    data: {
      pageAnalytics: PagesAnalytics.CONFIGURATION
    }
  },
  {
    path: 'office-service',
    loadChildren: () =>
      import('./office-service/office-service.module').then((m) => m.OfficeServiceComponentPageModule),
    canActivate: [OnBoardingGuardService, ExworkersGuardService],
    data: {
      pageAnalytics: PagesAnalytics.OFFICESERVICES
    }
  },
  {
    path: 'behaviour-code',
    loadChildren: () => import('./behaviour-code/behaviour-code.module').then((m) => m.BehaviourCodePageModule),
    canActivate: [ExworkersGuardService],
    data: {
      pageAnalytics: PagesAnalytics.BEHAVIOUR_CODE
    }
  },
  {
    path: 'codes',
    loadChildren: () => import('./ios-install-code/ios-install-code.module').then((m) => m.IosInstallCodePageModule),
    canLoad: [PlatformGuardService],
    data: { platforms: ['mobileweb', 'ios'], pageAnalytics: PagesAnalytics.IOS_CODES }
  },
  {
    path: 'close-app',
    loadChildren: () => import('./close-app/close-app.module').then((m) => m.CloseAppPageModule),
    canActivate: [OnBoardingGuardService, ExworkersGuardService],
    data: {
      pageAnalytics: PagesAnalytics.CLOSE_APP
    }
  },
  {
    path: 'data-treatment',
    loadChildren: () => import('./data-treatment/data-treatment.module').then((m) => m.DataTreatmentPageModule),
    canActivate: [PermissionsGuardService, ExworkersGuardService],
    data: {
      allowGoBack: false,
      pageAnalytics: PagesAnalytics.DATA_TREATMENT,
      pagePermissions: PTRAB_PERMISSIONS,
      redirectTo: PAGES.HOME
    }
  },
  {
    path: 'logistic-anual-calendar',
    loadChildren: () =>
      import('./logistic-anual-calendar/logistic-anual-calendar.module').then(
        (m) => m.LogisticAnualCalendarComponentModule
      ),
    canLoad: [AuthGuardService],
    canActivate: [OnBoardingGuardService, ExworkersGuardService],
    data: {
      allowGoBack: true,
      pageAnalytics: ''
    }
  },
  {
    path: 'my-publications',
    loadChildren: () =>
      import('./publications/my-publications/my-publications.module').then((m) => m.MyPublicationsPageModule),
    canLoad: [AuthGuardService],
    canActivate: [OnBoardingGuardService, ExworkersGuardService],
    data: {
      allowGoBack: true,
      pageAnalytics: PagesAnalytics.MY_PUBLICATIONS
    }
  },
  {
    path: 'my-publication/:id',
    loadChildren: () =>
      import('./publications/my-publications/my-publications-detail/my-publications-detail.module').then(
        (m) => m.MyPublicationsDetailPageModule
      ),
    canLoad: [AuthGuardService],
    canActivate: [OnBoardingGuardService, ExworkersGuardService],
    data: {
      type: PAGES.ITEM_MY_PUBLICATION,
      allowGoBack: true
    }
  },
  {
    path: 'create-publication',
    loadChildren: () =>
      import('./publications/create-publication/create-publication.module').then((m) => m.CreatePublicationPageModule),
    canActivate: [OnBoardingGuardService, ExworkersGuardService],
    data: {
      allowGoBack: true,
      pageAnalytics: PagesAnalytics.CREATE_PUBLICATION
    }
  },
  {
    path: 'office-floor-plan',
    loadChildren: () => import('./office-floor-plan/office-floor-plan.module').then((m) => m.OfficeFloorPlanPageModule),
    canActivate: [OnBoardingGuardService, ExworkersGuardService],
    data: {
      allowGoBack: true,
      pageAnalytics: PagesAnalytics.OFFICE_FLOOR_PLAN
    }
  },
  {
    path: 'on-boarding',
    loadChildren: () => import('./on-boarding/on-boarding.module').then((m) => m.OnBoardingPageModule),
    canActivate: [PermissionsGuardService, ExworkersGuardService],
    data: {
      pagePermissions: [Permissions.ONBOARDING],
      redirectTo: PAGES.HOME
      // pageAnalytics: PagesAnalytics.PERSONAL_DATA
    }
  },
  {
    path: 'manage-vacations',
    loadChildren: () =>
      import('../activo2/manage-vacations/manage-vacations.module').then((m) => m.ManageVacationsModule),
    canActivate: [PermissionsGuardService, ExworkersGuardService],
    data: {
      pagePermissions: MANAGE_VACATIONS_PERMISSIONS,
      pageToRedirectAfterLogin: getUrlFromMap(PAGES.VACATIONS_REQUEST),
      redirectTo: PAGES.HOME,
      pageAnalytics: PagesAnalytics.MANAGE_VACATIONS
    }
  },
  {
    path: 'vacations',
    loadChildren: () => import('../activo2/vacations/vacations.module').then((m) => m.VacationsModule),
    canActivate: [PermissionsGuardService, ExworkersGuardService],
    data: {
      allowGoBack: true,
      pagePermissions: VACATIONS_PERMISSIONS,
      pageToRedirectAfterLogin: getUrlFromMap(PAGES.VACATIONS),
      redirectTo: PAGES.HOME,
      pageAnalytics: PagesAnalytics.VACATIONS
    }
  },
  {
    path: 'vacations/detail',
    loadChildren: () =>
      import('../activo2/vacations/components/total-vacations-detail/total-vacations-detail.module').then(
        (m) => m.TotalVacationsDetailModule
      ),
    canActivate: [PermissionsGuardService, ExworkersGuardService],
    data: {
      allowGoBack: true,
      pagePermissions: VACATIONS_PERMISSIONS,
      pageToRedirectAfterLogin: getUrlFromMap(PAGES.VACATIONS_DETAIL),
      redirectTo: PAGES.HOME,
      pageAnalytics: PagesAnalytics.VACATIONS
    }
  },
  {
    path: 'home-exworkers',
    loadChildren: () => import('./home-exworkers/home-exworkers.module').then((m) => m.HomeExworkersPageModule),
    canActivate: [AuthGuardService, PermissionsGuardService],
    data: {
      pagePermissions: [Permissions.EXEMPLOYEE_PAYSLIPS],
      redirectTo: PAGES.HOME
    }
  }
];

@NgModule({
  imports: [RouterModule.forChild(routes)],
  exports: [RouterModule]
})
export class Activo2RoutingModule {}
